import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { StudentModal } from './StudentModal';
import { FamilyModal } from './FamilyModal';

interface Student {
  id: number;
  first_name: string;
  family_name: string;
  email: string;
  gender: string | null;
  home_country: string;
  major: string;
  special_interests: string;
  has_dietary_restrictions: boolean;
  semester_year: string;
  semester_id?: number;
}

interface Family {
  id: number;
  first_name: string;
  last_name: string;
  combined_name: string;
  email: string;
  address: string;
  phone_number: string;
  status: string;
}

interface Assignment {
  id: number;
  family_id: number;
  student_id: number;
  semester_id: number;
}

interface Semester {
  id: number;
  name: string;
  season: string;
  year: number;
  is_active: boolean;
}

interface FamilyAvailability {
  id: number;
  family_id: number;
  semester_id: number;
  max_students: number;
  preferences_notes: string | null;
}

interface MatchingDashboardProps {
  selectedSemesterId: number | null;
  selectedSeason: string;
  selectedYear: number;
}

export const MatchingDashboard: React.FC<MatchingDashboardProps> = ({ 
  selectedSemesterId,
  selectedSeason,
  selectedYear
}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [semesters, setSemesters] = useState<Semester[]>([]);
  const [familyAvailabilities, setFamilyAvailabilities] = useState<FamilyAvailability[]>([]);
  
  // Modal states
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  


  const loadAvailabilityData = useCallback(async () => {
    if (!selectedSemesterId) return;
    
    try {
      const { data } = await supabase
        .from('family_availability')
        .select('*')
        .eq('semester_id', selectedSemesterId);
      
      if (data) setFamilyAvailabilities(data);
    } catch (error) {
      console.error('Error loading availability data:', error);
    }
  }, [selectedSemesterId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsResult, familiesResult, assignmentsResult, semestersResult] = await Promise.all([
        supabase.from('students').select('*').order('time_submitted', { ascending: true }),
        supabase.from('families').select('*').eq('status', 'Active').order('last_name'),
        supabase.from('assignments').select('*'),
        supabase.from('semesters').select('*').order('year', { ascending: false }).order('season')
      ]);

      if (studentsResult.data) setStudents(studentsResult.data);
      if (familiesResult.data) setFamilies(familiesResult.data);
      if (assignmentsResult.data) setAssignments(assignmentsResult.data);
      
      if (semestersResult.data) setSemesters(semestersResult.data);

      // Load availability data if we have a semester ID
      if (selectedSemesterId) {
        const availabilityResult = await supabase
          .from('family_availability')
          .select('*')
          .eq('semester_id', selectedSemesterId);
        
        if (availabilityResult.data) setFamilyAvailabilities(availabilityResult.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedSemesterId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedSemesterId) {
      loadAvailabilityData();
    }
  }, [selectedSemesterId, loadAvailabilityData]);

  const getAssignedFamily = (studentId: number) => {
    const assignment = assignments.find(a => a.student_id === studentId && a.semester_id === selectedSemesterId);
    if (!assignment) return null;
    return families.find(f => f.id === assignment.family_id);
  };

  const getAssignedStudents = (familyId: number) => {
    const familyAssignments = assignments.filter(a => a.family_id === familyId && a.semester_id === selectedSemesterId);
    return familyAssignments.map(assignment => 
      students.find(s => s.id === assignment.student_id)
    ).filter(Boolean);
  };

  const getFamilyAvailability = (familyId: number) => {
    return familyAvailabilities.find(a => a.family_id === familyId);
  };

  const getFamilyCapacity = (familyId: number) => {
    const availability = getFamilyAvailability(familyId);
    const assignedStudents = getAssignedStudents(familyId);
    
    if (!availability) {
      return {
        maxStudents: 0,
        currentStudents: assignedStudents.length,
        remainingSlots: 0,
        isAvailable: false,
        isAtCapacity: true
      };
    }
    
    return {
      maxStudents: availability.max_students,
      currentStudents: assignedStudents.length,
      remainingSlots: availability.max_students - assignedStudents.length,
      isAvailable: availability.max_students > assignedStudents.length,
      isAtCapacity: availability.max_students <= assignedStudents.length,
      preferences: availability.preferences_notes
    };
  };

  const getCurrentSemester = () => {
    return semesters.find(s => s.id === selectedSemesterId);
  };

  const formatStudentName = (student: Student) => {
    const genderIndicator = student.gender?.toLowerCase() === 'male' ? ' (M)' : 
                           student.gender?.toLowerCase() === 'female' ? ' (F)' : '';
    return `${student.first_name} ${student.family_name}${genderIndicator}`;
  };

  const handleOpenStudentModal = (studentId: number) => {
    setSelectedStudentId(studentId);
    setStudentModalOpen(true);
  };

  const handleCloseStudentModal = () => {
    setStudentModalOpen(false);
    setSelectedStudentId(null);
  };

  const handleOpenFamilyModal = (familyId: number) => {
    setSelectedFamilyId(familyId);
    setFamilyModalOpen(true);
  };

  const handleCloseFamilyModal = () => {
    setFamilyModalOpen(false);
    setSelectedFamilyId(null);
  };

  const handleStudentUpdated = () => {
    loadData(); // Refresh data after student update
  };

  const handleFamilyUpdated = () => {
    loadData(); // Refresh data after family update
  };

  const handleAssignStudent = async (familyId: number) => {
    if (!selectedStudent || !selectedSemesterId) return;

    // Check capacity before assigning
    const capacity = getFamilyCapacity(familyId);
    if (!capacity.isAvailable) {
      alert(`This family is at capacity (${capacity.currentStudents}/${capacity.maxStudents} students). Please check their availability settings or assign to a different family.`);
      return;
    }

    try {
      if (!selectedSemesterId) {
        alert('No semester selected');
        return;
      }

      const { error } = await supabase.from('assignments').insert({
        family_id: familyId,
        student_id: selectedStudent.id,
        semester_id: selectedSemesterId,
        family_email_sent_at: null,
        student_email_sent_at: null
      });

      if (error) throw error;

      await loadData();
      setSelectedStudent(null);
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Error creating assignment');
    }
  };

  const handleUnassignStudent = async (studentId: number) => {
    try {
      const { error } = await supabase
        .from('assignments')
        .delete()
        .eq('student_id', studentId)
        .eq('semester_id', selectedSemesterId);

      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error removing assignment:', error);
    }
  };


  // Filter students by current semester
  const currentSemester = getCurrentSemester();
  const filteredStudents = students.filter(student => {
    if (!selectedSemesterId) return false;
    // Use semester_id if available, otherwise fall back to semester_year string matching
    if (student.semester_id) {
      return student.semester_id === selectedSemesterId;
    } else if (student.semester_year && currentSemester) {
      return student.semester_year === currentSemester.name;
    }
    return false;
  });

  // Filter families to show only those with availability for the current semester
  const filteredFamilies = families.filter(family => {
    const availability = familyAvailabilities.find(a => a.family_id === family.id);
    return availability !== undefined;
  });

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <h2>Matching Dashboard - {selectedSeason} {selectedYear}</h2>
        
        <div style={{ color: '#ccc', fontSize: '0.9em' }}>
          Showing {filteredStudents.length} students
          {familyAvailabilities.length > 0 && (
            <>
              {' • '}
              {familyAvailabilities.reduce((sum, a) => sum + a.max_students, 0)} total slots available
              {' • '}
              {familyAvailabilities.reduce((sum, a) => sum + (getAssignedStudents(a.family_id).length), 0)} slots filled
            </>
          )}
        </div>
      </div>

      {selectedStudent && (
        <div style={{ 
          background: '#f0f8ff', 
          padding: '15px', 
          marginBottom: '20px', 
          borderRadius: '8px',
          color: '#333'
        }}>
          <strong>Selected Student:</strong> {formatStudentName(selectedStudent)} 
          <button 
            onClick={() => setSelectedStudent(null)}
            style={{ marginLeft: '10px', padding: '5px 10px' }}
          >
            Cancel
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '40px', height: '70vh' }}>
        
        {/* Students Column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <h3>Students ({filteredStudents.length})</h3>
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            border: '1px solid #ccc', 
            borderRadius: '8px',
            backgroundColor: '#1a1a1a'
          }}>
            {filteredStudents.map(student => {
              const assignedFamily = getAssignedFamily(student.id);
              const isAssigned = !!assignedFamily;
              
              return (
                <div 
                  key={student.id}
                  style={{
                    padding: '15px',
                    margin: '10px',
                    borderRadius: '8px',
                    border: selectedStudent?.id === student.id ? '3px solid #007bff' : 
                            isAssigned ? '2px solid #28a745' : '1px solid #666',
                    backgroundColor: isAssigned ? '#1e3a1e' : '#2a2a2a',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                  onClick={() => !isAssigned && setSelectedStudent(student)}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    <span 
                      style={{ 
                        cursor: 'pointer', 
                        textDecoration: 'underline',
                        color: '#87CEEB'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenStudentModal(student.id);
                      }}
                    >
                      {formatStudentName(student)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9em', color: '#ccc' }}>
                    <div>{student.home_country} • {student.major}</div>
                    <div style={{ marginTop: '5px' }}>{student.special_interests}</div>
                    {student.has_dietary_restrictions && (
                      <div style={{ color: '#ffa500', marginTop: '5px' }}>⚠️ Dietary restrictions</div>
                    )}
                  </div>
                  {isAssigned && (
                    <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#28a745', borderRadius: '4px' }}>
                      ✅ Assigned to: <span 
                        style={{ 
                          cursor: 'pointer', 
                          textDecoration: 'underline',
                          color: '#90EE90'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (assignedFamily) handleOpenFamilyModal(assignedFamily.id);
                        }}
                      >
                        {assignedFamily?.combined_name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnassignStudent(student.id);
                        }}
                        style={{ 
                          marginLeft: '10px', 
                          padding: '2px 8px', 
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Families Column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <h3>Host Families ({filteredFamilies.length})</h3>
          <div style={{ fontSize: '0.8em', color: '#999', marginBottom: '10px' }}>
            Showing families with availability for {currentSemester?.name || 'this semester'}
          </div>
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            border: '1px solid #ccc', 
            borderRadius: '8px',
            backgroundColor: '#1a1a1a'
          }}>
            {filteredFamilies.map(family => {
              const assignedStudents = getAssignedStudents(family.id);
              const capacity = getFamilyCapacity(family.id);
              const isAssigned = assignedStudents.length > 0;
              
              return (
                <div 
                  key={family.id}
                  style={{
                    padding: '15px',
                    margin: '10px',
                    borderRadius: '8px',
                    border: capacity.isAtCapacity ? '2px solid #ffc107' : 
                            isAssigned ? '2px solid #28a745' : 
                            capacity.maxStudents > 0 ? '2px solid #007bff' : '1px solid #666',
                    backgroundColor: selectedStudent && capacity.isAvailable ? '#2a1a3a' : 
                                   capacity.isAtCapacity ? '#3a3a1e' :
                                   isAssigned ? '#1e3a1e' : 
                                   capacity.maxStudents > 0 ? '#1e2a3a' : '#2a2a2a',
                    cursor: selectedStudent && capacity.isAvailable ? 'pointer' : 'default',
                    color: 'white',
                    opacity: selectedStudent && !capacity.isAvailable ? 0.6 : 1
                  }}
                  onClick={() => selectedStudent && capacity.isAvailable && handleAssignStudent(family.id)}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span 
                      style={{ 
                        cursor: 'pointer', 
                        textDecoration: 'underline',
                        color: '#90EE90'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenFamilyModal(family.id);
                      }}
                    >
                      {family.combined_name}
                    </span>
                    <div style={{ fontSize: '0.8em', fontWeight: 'normal' }}>
                      {capacity.maxStudents > 0 ? (
                        <span style={{ 
                          padding: '2px 6px', 
                          borderRadius: '3px',
                          backgroundColor: capacity.isAtCapacity ? '#ffc107' : 
                                         capacity.remainingSlots === 1 ? '#fd7e14' : '#28a745',
                          color: capacity.isAtCapacity ? '#000' : '#fff'
                        }}>
                          {capacity.currentStudents}/{capacity.maxStudents}
                        </span>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.8em' }}>No availability</span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.9em', color: '#ccc' }}>
                    <div>{family.address}</div>
                    <div>{family.email}</div>
                    <div>{family.phone_number}</div>
                  </div>
                  
                  {capacity.preferences && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px', 
                      backgroundColor: '#2a2a3a', 
                      borderRadius: '4px',
                      border: '1px solid #444',
                      fontSize: '0.85em'
                    }}>
                      <div style={{ 
                        fontSize: '0.75em', 
                        color: '#999', 
                        fontWeight: 'bold',
                        marginBottom: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Preferences & Notes
                      </div>
                      <div style={{ color: '#ddd', fontStyle: 'italic', lineHeight: '1.3' }}>
                        {capacity.preferences}
                      </div>
                    </div>
                  )}
                  {assignedStudents.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ 
                        padding: '8px', 
                        backgroundColor: '#28a745', 
                        borderRadius: '4px',
                        marginBottom: '5px'
                      }}>
                        ✅ Hosting {assignedStudents.length} student{assignedStudents.length > 1 ? 's' : ''}:
                      </div>
                      {assignedStudents.map((student, index) => (
                        <div 
                          key={student?.id || index}
                          style={{ 
                            padding: '5px 8px', 
                            backgroundColor: '#1e4a1e', 
                            borderRadius: '3px',
                            marginBottom: '3px',
                            fontSize: '0.9em',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span 
                            style={{ 
                              cursor: student ? 'pointer' : 'default', 
                              textDecoration: student ? 'underline' : 'none',
                              color: student ? '#87CEEB' : 'inherit'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (student) handleOpenStudentModal(student.id);
                            }}
                          >
                            {student ? formatStudentName(student) : 'Unknown Student'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (student?.id) handleUnassignStudent(student.id);
                            }}
                            style={{ 
                              padding: '2px 6px', 
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '2px',
                              fontSize: '0.8em'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedStudent && (
                    <div style={{ marginTop: '10px' }}>
                      {capacity.isAvailable ? (
                        <div style={{ color: '#007bff' }}>
                          Click to assign {selectedStudent.first_name} to this family
                        </div>
                      ) : (
                        <div style={{ color: '#ffc107' }}>
                          Family at capacity - cannot assign more students
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Student Modal */}
      {selectedStudentId && (
        <StudentModal
          studentId={selectedStudentId}
          isOpen={studentModalOpen}
          onClose={handleCloseStudentModal}
          onStudentUpdated={handleStudentUpdated}
        />
      )}

      {/* Family Modal */}
      {selectedFamilyId && (
        <FamilyModal
          familyId={selectedFamilyId}
          isOpen={familyModalOpen}
          onClose={handleCloseFamilyModal}
          onFamilyUpdated={handleFamilyUpdated}
        />
      )}
    </div>
  );
};